import type {Middleware, UnknownAction} from '@reduxjs/toolkit';
import {isRejectedWithValue} from '@reduxjs/toolkit';
import {
    createAuthState,
    getStoredAuthState,
    isRefreshTokenExpired,
} from '@utils/auth';
import {getOrCreateDeviceId} from '@utils/storage';
import {
    FetchArgs,
    fetchBaseQuery,
    FetchBaseQueryArgs,
} from '@reduxjs/toolkit/query/react';

import type {BaseQueryApi} from '@reduxjs/toolkit/query';
import {authSlice} from '@goproblems/shared/state/auth';
import {Mutex} from 'async-mutex';
import type {AuthState} from '@types';
import {
    getBrowserAcceptLanguageHeader,
    isBrowserDefaultLanguage,
} from '@utils/language';

const PASSWORD_CHANGE_REQUIRED = 'PASSWORD_CHANGE_REQUIRED';

type AuthStoreState = {
    auth: AuthState;
};

type ErrorPayload = {
    status?: number | string;
    data?: {
        code?: string;
        message?: string;
        error?: string;
        redirectTo?: string;
    };
};

type PasswordChangeRequiredPayload = {
    code?: string;
    redirectTo?: string;
};

const mutex = new Mutex();

export const API_URL = import.meta.env.VITE_API_URL ?? '';

const getCurrentAuthState = (state: AuthStoreState): AuthState | null => {
    const storedAuth = getStoredAuthState();
    if (storedAuth !== null) {
        return storedAuth;
    }

    const stateAuth = createAuthState(state.auth);
    if (stateAuth === null || isRefreshTokenExpired(stateAuth)) {
        return null;
    }

    return stateAuth;
};

const prepareHeaders = (
    headers: Headers,
    {getState}: Pick<BaseQueryApi, 'getState'>
): Headers => {
    const auth = getCurrentAuthState(getState() as AuthStoreState);
    const token = auth?.token;
    const authLanguage = auth?.user?.language;

    if (token) {
        headers.set('Authorization', `Bearer ${token}`);
    }

    headers.set('Accept', 'application/json');
    if (!headers.has('Accept-Language')) {
        headers.set(
            'Accept-Language',
            authLanguage && !isBrowserDefaultLanguage(authLanguage)
                ? authLanguage
                : getBrowserAcceptLanguageHeader()
        );
    }
    headers.set('X-Device-Id', getOrCreateDeviceId());

    return headers;
};

export const API_BASE_CONFIG = {
    baseUrl: `${API_URL}/api`,
    prepareHeaders,
};

export const API_V2_BASE_CONFIG = {
    baseUrl: `${API_URL}/api/v2`,
    prepareHeaders,
};

const getPasswordChangeRedirect = (data: unknown): string | null => {
    if (!data || typeof data !== 'object') {
        return null;
    }

    const payload = data as PasswordChangeRequiredPayload;
    if (payload.code !== PASSWORD_CHANGE_REQUIRED) {
        return null;
    }

    return payload.redirectTo || '/settings/password';
};

// @TODO 'Accept': 'application/json' should be presented for exception listeners
export const fetch = (args: FetchBaseQueryArgs) => {
    const baseQuery = fetchBaseQuery(args);

    return (async (
        fetchArgs: FetchArgs | string,
        api: BaseQueryApi,
        extraOptions: {}
    ) => {
        // Wait if another request is already refreshing the token
        await mutex.waitForUnlock();

        let result = await baseQuery(fetchArgs, api, extraOptions);

        if (result.error && result.error.status === 401) {
            const stateAuth = (api.getState() as AuthStoreState).auth;
            // Only attempt token refresh if we were using JWT auth. If a
            // stale auth user exists without a token, clear it instead.
            const auth = getCurrentAuthState(api.getState() as AuthStoreState);
            const hadToken = !!auth?.token;
            const hadAuthIdentity =
                hadToken || !!auth?.user || !!stateAuth.user;

            if (hadToken) {
                // Check if another request is already refreshing
                if (!mutex.isLocked()) {
                    const release = await mutex.acquire();
                    try {
                        // Re-read auth in case another request already refreshed
                        const freshAuth = getCurrentAuthState(
                            api.getState() as AuthStoreState
                        );
                        const refreshToken = freshAuth?.refresh_token;

                        if (refreshToken) {
                            const refreshBaseQuery = fetchBaseQuery({
                                baseUrl: `${API_URL}/api`,
                                prepareHeaders: (headers: Headers) => {
                                    headers.set('Accept', 'application/json');
                                    return headers;
                                },
                            });

                            const refreshResult = await refreshBaseQuery(
                                {
                                    url: '/auth/refresh',
                                    method: 'POST',
                                    body: {refresh_token: refreshToken},
                                },
                                {
                                    ...api,
                                    endpoint: 'refreshToken',
                                },
                                extraOptions
                            );

                            if (
                                refreshResult.data &&
                                typeof refreshResult.data === 'object' &&
                                'token' in
                                    (refreshResult.data as Record<
                                        string,
                                        unknown
                                    >)
                            ) {
                                const newAuth = refreshResult.data as {
                                    token?: string | null;
                                    refresh_token?: string | null;
                                    refresh_token_expiration?: number | null;
                                };
                                const nextAuth = createAuthState({
                                    user: freshAuth?.user ?? stateAuth.user,
                                    token: newAuth.token,
                                    refresh_token: newAuth.refresh_token,
                                    refresh_token_expiration:
                                        newAuth.refresh_token_expiration ??
                                        freshAuth?.refresh_token_expiration ??
                                        stateAuth.refresh_token_expiration ??
                                        null,
                                });

                                if (nextAuth === null) {
                                    api.dispatch(authSlice.actions.logout());
                                    return result;
                                }

                                api.dispatch(
                                    authSlice.actions.setAuth(nextAuth)
                                );
                                // Retry the original request with new token
                                result = await baseQuery(
                                    fetchArgs,
                                    api,
                                    extraOptions
                                );
                                if (
                                    result.error &&
                                    result.error.status === 401
                                ) {
                                    api.dispatch(authSlice.actions.logout());
                                }
                            } else if (refreshResult.error) {
                                if (refreshResult.error.status === 401) {
                                    api.dispatch(authSlice.actions.logout());
                                } else {
                                    result = refreshResult;
                                }
                            }
                        } else {
                            // No refresh token available — log out
                            api.dispatch(authSlice.actions.logout());
                        }
                    } finally {
                        release();
                    }
                } else {
                    // Another request is already refreshing — wait and retry
                    await mutex.waitForUnlock();
                    result = await baseQuery(fetchArgs, api, extraOptions);
                }
            } else if (hadAuthIdentity) {
                api.dispatch(authSlice.actions.logout());
            }
            // else: no JWT auth state was set, so pass through the 401.
        }

        const passwordChangeRedirect = result.error
            ? getPasswordChangeRedirect(result.error.data)
            : null;
        if (
            passwordChangeRedirect &&
            window.location.pathname !== passwordChangeRedirect
        ) {
            window.location.href = passwordChangeRedirect;
        }

        return result;
    }) as typeof baseQuery;
};

/**
 * Log a warning and show a toast
 */
// TODO: To be integrated with the notification system
export const rtkQueryErrorLogger: Middleware =
    () => next => (action: unknown) => {
        const act = action as UnknownAction;
        // RTK Query uses `createAsyncThunk` from redux-toolkit under the hood, so we're able to utilize these matchers!
        // Detect rejected actions either created with `rejectWithValue` (isRejectedWithValue)
        // or regular rejected actions dispatched by RTK Query (their type ends with '/rejected').
        if (
            isRejectedWithValue(act) ||
            String(act.type).endsWith('/rejected')
        ) {
            const payload = act.payload as ErrorPayload | undefined;
            const error = act.error as
                | {status?: number | string; message?: string}
                | undefined;

            const status = payload?.status ?? error?.status ?? null;
            const message =
                payload?.data?.message ||
                payload?.data?.error ||
                error?.message;

            console.warn(`RTK Query rejected (status: ${status}) - ${message}`);
            if (payload) {
                console.warn('RTK Query payload:', payload);
            }
        }

        return next(action);
    };
